import { S, PAPER as P } from "../customers/theme";
import { downloadStatementPDF } from "../lib/statementPdf";
import { todayISO } from "../lib/dates";

const money = (n) => `€${(Number(n) || 0).toFixed(2)}`;
const TYPE = { invoice: "Invoice", payment: "Payment received", credit_note: "Credit note" };

// Print only the statement (hide app chrome / sidebar / toolbar buttons).
const PRINT_CSS = `@media print {
  body * { visibility: hidden !important; }
  .statement-print, .statement-print * { visibility: visible !important; }
  .statement-print { position: absolute !important; left: 0; top: 0; width: 100% !important; margin: 0 !important; border-radius: 0 !important; box-shadow: none !important; }
  .no-print { display: none !important; }
}`;

export default function StatementView({ statement, business = {}, periodLabel, periodPicker, onClose }) {
  const s = statement;
  const cust = s.customer || {};
  const biz = business || {};
  const bizName = biz.trading_name || biz.legal_name || "Your Business";
  const bizAddr = [biz.address_line1, biz.address_line2, [biz.city, biz.county].filter(Boolean).join(", "), biz.postal_code, biz.country].filter(Boolean);
  const a = s.aging || {};
  const txns = s.transactions || [];

  const agingCols = [
    { k: "current", label: "Current" },
    { k: "d1_30", label: "1–30" },
    { k: "d31_60", label: "31–60" },
    { k: "d61_90", label: "61–90", color: P.amber },
    { k: "d90_plus", label: "90+", color: P.danger },
  ];

  const th = { textAlign: "left", padding: "7px 10px", background: P.head, color: P.muted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", borderBottom: `1px solid ${P.line}` };
  const thR = { ...th, textAlign: "right" };
  const td = { padding: "7px 10px", borderBottom: `1px solid ${P.line}`, fontSize: 12.5 };
  const tdR = { ...td, textAlign: "right" };

  return (
    <div style={S.modal} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <style>{PRINT_CSS}</style>
      <div style={{ ...S.modalBox, width: 880, background: "transparent", padding: 0, boxShadow: "none", maxHeight: "92vh", overflow: "auto" }}>
        {/* Toolbar — never printed */}
        <div className="no-print" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 10, flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>{periodPicker}</div>
          <div style={{ display: "flex", gap: 8 }}>
            <button style={S.btn()} onClick={() => downloadStatementPDF(s, business)}>⬇ Download PDF</button>
            <button style={S.btn("secondary")} onClick={() => window.print()}>🖨 Print</button>
            <button style={S.btn("secondary")} onClick={onClose}>Close</button>
          </div>
        </div>

        {/* The document */}
        <div className="statement-print" style={{ background: P.paper, color: P.ink, borderRadius: 10, padding: 30 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 20 }}>
            <div style={{ fontSize: 12.5, lineHeight: 1.6 }}>
              <div style={{ fontSize: 16, fontWeight: 800 }}>{bizName}</div>
              {bizAddr.map((l, i) => <div key={i} style={{ color: P.muted }}>{l}</div>)}
              {biz.email && <div style={{ color: P.muted }}>{biz.email}</div>}
              {biz.vat_number && <div style={{ color: P.muted }}>VAT {biz.vat_number}</div>}
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: "0.03em" }}>STATEMENT</div>
              <div style={{ color: P.muted, fontSize: 12.5, marginTop: 4 }}>Statement date: {s.period?.to || todayISO()}</div>
              <div style={{ color: P.muted, fontSize: 12.5 }}>Period: {periodLabel}</div>
            </div>
          </div>

          <div style={{ marginTop: 20, padding: "12px 14px", background: P.head, borderRadius: 8, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
            <div style={{ fontSize: 12.5, lineHeight: 1.6 }}>
              <div style={{ color: P.muted, fontSize: 11, fontWeight: 700, textTransform: "uppercase" }}>Statement for</div>
              <div style={{ fontWeight: 700 }}>{cust.company_name || "—"}{cust.customer_number && <span style={{ color: P.muted, fontWeight: 400 }}> · {cust.customer_number}</span>}</div>
              {cust.address && <div style={{ color: P.muted, whiteSpace: "pre-wrap" }}>{cust.address}</div>}
              {cust.email && <div style={{ color: P.muted }}>{cust.email}</div>}
              {cust.vat_number && <div style={{ color: P.muted }}>VAT {cust.vat_number}</div>}
            </div>
            <div style={{ textAlign: "right", alignSelf: "flex-end" }}>
              <div style={{ color: P.muted, fontSize: 11, fontWeight: 700, textTransform: "uppercase" }}>{s.in_credit ? "In credit" : "Total outstanding"}</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: s.in_credit ? P.ok : (s.current_outstanding > 0 ? P.ink : P.ok) }}>{money(Math.abs(s.current_outstanding))}{s.in_credit && <span style={{ fontSize: 13, fontWeight: 700 }}> CR</span>}</div>
            </div>
          </div>

          {/* Transactions */}
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 18 }}>
            <thead><tr>
              <th style={th}>Date</th><th style={th}>Reference</th><th style={th}>Description</th>
              <th style={thR}>Debit</th><th style={thR}>Credit</th><th style={thR}>Balance</th>
            </tr></thead>
            <tbody>
              <tr>
                <td style={td}>{s.period?.from || "—"}</td><td style={td} /><td style={{ ...td, fontStyle: "italic", color: P.muted }}>Opening balance</td>
                <td style={tdR} /><td style={tdR} /><td style={{ ...tdR, fontWeight: 700 }}>{money(s.opening_balance)}</td>
              </tr>
              {txns.length === 0 ? (
                <tr><td style={{ ...td, textAlign: "center", color: P.muted }} colSpan={6}>No transactions in this period.</td></tr>
              ) : txns.map((t, i) => (
                <tr key={i}>
                  <td style={td}>{t.date || "—"}</td>
                  <td style={{ ...td, fontFamily: "monospace", fontSize: 11.5 }}>{t.ref || "—"}</td>
                  <td style={td}>{t.description || TYPE[t.type] || t.type}</td>
                  <td style={tdR}>{t.debit ? money(t.debit) : ""}</td>
                  <td style={{ ...tdR, color: t.credit ? P.ok : P.ink }}>{t.credit ? money(t.credit) : ""}</td>
                  <td style={{ ...tdR, fontWeight: 600 }}>{money(t.running_balance)}</td>
                </tr>
              ))}
              <tr>
                <td style={{ ...td, borderTop: `2px solid ${P.ink}` }} colSpan={5}><strong>Closing balance</strong></td>
                <td style={{ ...tdR, borderTop: `2px solid ${P.ink}`, fontWeight: 800, fontSize: 14 }}>{money(s.closing_balance)}</td>
              </tr>
            </tbody>
          </table>

          {/* Aging */}
          <div style={{ marginTop: 22 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: P.muted, marginBottom: 6 }}>Aging summary</div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>{agingCols.map((col) => <th key={col.k} style={{ ...thR, color: col.color || P.muted }}>{col.label}</th>)}<th style={thR}>Total</th></tr></thead>
              <tbody><tr>
                {agingCols.map((col) => <td key={col.k} style={{ ...tdR, color: col.color || P.ink, fontWeight: col.color ? 700 : 400 }}>{money(a[col.k])}</td>)}
                <td style={{ ...tdR, fontWeight: 800 }}>{money(a.total)}</td>
              </tr></tbody>
            </table>
          </div>

          {(biz.iban || biz.bank_name) && (
            <div style={{ marginTop: 20, fontSize: 11.5, color: P.muted, borderTop: `1px solid ${P.line}`, paddingTop: 10 }}>
              Please remit payment to: {biz.bank_account_name || bizName}{biz.bank_name && ` · ${biz.bank_name}`}{biz.iban && ` · IBAN ${biz.iban}`}{biz.bic && ` · BIC ${biz.bic}`}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
